import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { PaperBlurFitBtn } from '../../../shared/component/headless/button/BtnBody';
import { useAdminMaterialAlias } from '../../share/hook/useAdminMaterialAlias';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { AlertPosition, AlertType } from '../../../shared/enum/EnumRepository';
import { getAlertMessage } from '../../../shared/constants/alertMessages';

const MaterialAliasViewer: React.FC = () => {
  const {
    activeAlias,
    aliasList,
    aliasSetList,
    page,
    pageSize,
    unassignedOnly,
    sortField,
    sortDirection,
    load,
    promote,
    assign,
    remove,
  } = useAdminMaterialAlias();
  const enqueueAlert = useStandardAlertStore((state) => state.enqueueAlert);
  const [material, setMaterial] = useState('');
  const [materialEn, setMaterialEn] = useState('');
  const [aliasSetId, setAliasSetId] = useState('');

  useEffect(() => {
    setMaterial(activeAlias?.aliasSet?.material ?? activeAlias?.keyword ?? '');
    setMaterialEn(activeAlias?.aliasSet?.materialEn ?? '');
    setAliasSetId(activeAlias?.aliasSet?.id ?? '');
  }, [activeAlias]);

  if (!activeAlias) return null;

  const reloadFilteredList = async () => {
    if (!unassignedOnly) return;
    await load(page, unassignedOnly, Number(pageSize));
  };

  const handlePromote = async () => {
    if (!material.trim()) return;
    await promote(activeAlias.id, {
      material: material.trim(),
      materialEn: materialEn.trim(),
    });
    await reloadFilteredList();
  };

  const handleAssign = async () => {
    if (!aliasSetId) return;
    await assign(activeAlias.id, aliasSetId);
    await reloadFilteredList();
  };

  const handleDelete = () => {
    const aliasId = activeAlias.id;
    const targetPage = aliasList.length === 1 && page > 1 ? page - 1 : page;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_MATERIAL_ALIAS', {
        keyword: activeAlias.keyword,
      }),
      callBack: async () => {
        const deleted = await remove(aliasId);
        if (!deleted) return;
        await load(
          targetPage,
          unassignedOnly,
          Number(pageSize),
          sortDirection,
          sortField,
        );
      },
    });
  };

  return (
    <ViewerContainer>
      <EditorHeader>
        <EditorTitle>{activeAlias.keyword}</EditorTitle>
        <Meta>{activeAlias.frequency} detected</Meta>
      </EditorHeader>

      <Section>
        <SectionTitle>New alias set</SectionTitle>
        <FieldRow>
          <Label>Orig</Label>
          <Input
            value={material}
            onChange={(event) => setMaterial(event.target.value)}
            placeholder="Material"
          />
        </FieldRow>
        <FieldRow>
          <Label>ENG</Label>
          <Input
            value={materialEn}
            onChange={(event) => setMaterialEn(event.target.value)}
            placeholder="Material (optional)"
          />
        </FieldRow>
        <ButtonRow>
          <ActionButton onClick={handlePromote} disabled={!material.trim()}>
            Promote
          </ActionButton>
        </ButtonRow>
      </Section>

      <Section>
        <SectionTitle>Existing alias set</SectionTitle>
        <FieldRow>
          <Label>Set</Label>
          <Select
            value={aliasSetId}
            onChange={(event) => setAliasSetId(event.target.value)}
          >
            <option value="">Select alias set</option>
            {aliasSetList.map((aliasSet) => (
              <option key={aliasSet.id} value={aliasSet.id}>
                {aliasSet.material}
                {aliasSet.materialEn ? ` / ${aliasSet.materialEn}` : ''}
              </option>
            ))}
          </Select>
        </FieldRow>
        <ButtonRow>
          <ActionButton onClick={handleAssign} disabled={!aliasSetId}>
            Assign
          </ActionButton>
        </ButtonRow>
      </Section>

      <DeleteButtonRow>
        <ActionButton onClick={handleDelete}>Delete</ActionButton>
      </DeleteButtonRow>
    </ViewerContainer>
  );
};

const ViewerContainer = styled.div`
  position: relative;
  width: 29vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
  padding-bottom: 40px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};

  &::-webkit-scrollbar {
    display: none;
  }
`;

const EditorHeader = styled.div`
  padding: 4px 0 18px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const EditorTitle = styled.h2`
  min-height: 28px;
  margin: 0;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_02_1};
`;

const Meta = styled.div`
  color: ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const Section = styled.section`
  padding: 18px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const SectionTitle = styled.div`
  margin-bottom: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const FieldRow = styled.label`
  min-height: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const Label = styled.span`
  width: 44px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const Input = styled.input`
  min-width: 0;
  flex: 1;
  height: 24px;
  padding: 0 2px;
  border: 0;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_03};
  outline: none;
  background: transparent;
  color: inherit;
  ${({ theme }) => theme.typography.Body_04};
`;

const Select = styled.select`
  min-width: 0;
  flex: 1;
  height: 24px;
  padding: 0 2px;
  border: 0;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_03};
  outline: none;
  background: transparent;
  color: inherit;
  ${({ theme }) => theme.typography.Body_04};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
`;

const DeleteButtonRow = styled(ButtonRow)`
  padding-top: 18px;
`;

const ActionButton = styled(PaperBlurFitBtn)`
  &:disabled {
    cursor: default;
    opacity: 0.3;
    transform: none;
  }
`;

export default MaterialAliasViewer;
